import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, DatePicker, Input, Result, Select, Table, Tag, Tooltip } from "antd";
import { CheckOutlined, ReloadOutlined, SearchOutlined, UserAddOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { usePermissions } from "../../utils/usePermissions";
import { isSuperAdmin } from "../../utils/permissions";
import { BASE_API_URL } from "../../utils/consts";
import AssignOctopusShiftModal from "./AssignOctopusShiftModal";

interface InternalUser {
  _id: string;
  firstName?: string;
  lastName?: string;
  username?: string;
}

const { RangePicker } = DatePicker;

interface TokenOption {
  _id: string;
  name: string;
  locationId?: { _id: string; name: string } | null;
}

interface UserRegistration {
  Id: number;
  UserFullName: string;
  CheckIn: string;
  CheckOut?: string;
  AlreadyAssigned?: boolean;
}

const normalize = (s: string) =>
  (s || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

const OctopusShifts: React.FC = () => {
  const { t } = useTranslation();
  const userData = useSelector((state: any) => state.userData.user);
  const rawClientId = userData?.clientId;
  const clientId =
    typeof rawClientId === "object" ? rawClientId?._id : rawClientId;

  const { hasPermission } = usePermissions();
  const canView =
    isSuperAdmin(userData?.roles) || hasPermission("octopus-shifts:view");
  const canAssign =
    isSuperAdmin(userData?.roles) || hasPermission("shifts:create-single");

  const [internalUsers, setInternalUsers] = useState<InternalUser[]>([]);
  const [assignedIds, setAssignedIds] = useState<Set<number>>(new Set());
  const [assignRecord, setAssignRecord] = useState<UserRegistration | null>(null);

  const [tokens, setTokens] = useState<TokenOption[]>([]);
  const [selectedTokenId, setSelectedTokenId] = useState<string | undefined>();
  const [tokensLoading, setTokensLoading] = useState(false);

  const [dateRange, setDateRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("day"),
    dayjs().endOf("day"),
  ]);

  const [shifts, setShifts] = useState<UserRegistration[]>([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [sortedInfo, setSortedInfo] = useState<any>({ columnKey: "CheckIn", order: "descend" });

  useEffect(() => {
    if (!clientId || !canView) return;
    setTokensLoading(true);
    axios
      .get(`${BASE_API_URL}/${clientId}/octopus-tokens`)
      .then((res) => {
        setTokens(res.data || []);
        if (res.data?.length === 1) setSelectedTokenId(res.data[0]._id);
      })
      .catch(() => {})
      .finally(() => setTokensLoading(false));
  }, [clientId]);

  useEffect(() => {
    if (!clientId || !canAssign) return;
    axios
      .get(`${BASE_API_URL}/${clientId}/users`)
      .then((res) => setInternalUsers(Array.isArray(res.data) ? res.data : []))
      .catch(() => {});
  }, [clientId, canAssign]);

  const selectedLocationId = tokens.find((tk) => tk._id === selectedTokenId)
    ?.locationId?._id;

  const handleFetch = async () => {
    setLoading(true);
    setSearched(true);
    setAssignedIds(new Set());
    try {
      const token = tokens.find((tk) => tk._id === selectedTokenId);
      const dateFrom = dateRange[0].format("YYYY-MM-DD");
      // Octopus treats "dateTo" as an exclusive boundary, so ask for the day
      // after the selected end date to include the whole last day.
      const dateTo = dateRange[1].add(1, "day").format("YYYY-MM-DD");
      const params: Record<string, string> = { dateFrom, dateTo };
      if (token?.locationId?._id) params.locationId = token.locationId._id;

      const res = await axios.get(
        `${BASE_API_URL}/${clientId}/octopus-tokens/user-registrations`,
        { params }
      );
      setShifts(Array.isArray(res.data) ? res.data : []);
    } catch (err: any) {
      setShifts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTokenId) handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTokenId]);

  const isAssigned = (record: UserRegistration) =>
    !!record.AlreadyAssigned || assignedIds.has(record.Id);

  const displayedShifts = useMemo(() => {
    if (!searchText.trim()) return shifts;
    const q = normalize(searchText);
    return shifts.filter((s) => normalize(s.UserFullName).includes(q));
  }, [shifts, searchText]);

  const activeCount = displayedShifts.filter((s) => !s.CheckOut).length;
  const finishedCount = displayedShifts.filter((s) => s.CheckOut).length;

  if (!canView) {
    return (
      <Result
        status="403"
        title="403"
        subTitle={t("You don't have permission to view this page.")}
      />
    );
  }

  const columns = [
    {
      title: "Ime",
      dataIndex: "UserFullName",
      key: "UserFullName",
      sorter: (a: UserRegistration, b: UserRegistration) =>
        (a.UserFullName || "").localeCompare(b.UserFullName || "", "sr"),
      sortOrder: sortedInfo.columnKey === "UserFullName" ? sortedInfo.order : null,
    },
    {
      title: "Prijava",
      dataIndex: "CheckIn",
      key: "CheckIn",
      sorter: (a: UserRegistration, b: UserRegistration) =>
        dayjs(a.CheckIn).unix() - dayjs(b.CheckIn).unix(),
      sortOrder: sortedInfo.columnKey === "CheckIn" ? sortedInfo.order : null,
      render: (d: string) => (d ? dayjs(d).format("DD.MM.YYYY HH:mm") : "—"),
    },
    {
      title: "Odjava",
      dataIndex: "CheckOut",
      key: "CheckOut",
      sorter: (a: UserRegistration, b: UserRegistration) =>
        (a.CheckOut ? dayjs(a.CheckOut).unix() : 0) - (b.CheckOut ? dayjs(b.CheckOut).unix() : 0),
      sortOrder: sortedInfo.columnKey === "CheckOut" ? sortedInfo.order : null,
      render: (d: string) => (d ? dayjs(d).format("DD.MM.YYYY HH:mm") : "—"),
    },
    {
      title: "Status",
      key: "Status",
      filters: [
        { text: "Aktivna", value: "active" },
        { text: "Završena", value: "finished" },
      ],
      onFilter: (value: any, record: UserRegistration) =>
        value === "finished" ? !!record.CheckOut : !record.CheckOut,
      render: (_: any, record: UserRegistration) =>
        record.CheckOut ? (
          <Tag color="default">Završena</Tag>
        ) : (
          <Tag color="green">Aktivna</Tag>
        ),
    },
    ...(canAssign
      ? [
          {
            title: "",
            key: "assign",
            width: 160,
            render: (_: any, record: UserRegistration) => {
              if (isAssigned(record)) {
                return (
                  <Tag icon={<CheckOutlined />} color="blue">
                    Dodato
                  </Tag>
                );
              }
              if (!record.CheckOut) {
                return (
                  <Tooltip title="Smjena još nije završena (nema odjave)">
                    <Button size="small" disabled icon={<UserAddOutlined />}>
                      Dodaj
                    </Button>
                  </Tooltip>
                );
              }
              return (
                <Button
                  size="small"
                  icon={<UserAddOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    setAssignRecord(record);
                  }}
                >
                  Dodaj
                </Button>
              );
            },
          },
        ]
      : []),
  ];

  return (
    <div style={{ padding: "24px 24px 0" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 24,
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}>
          Smene
        </h1>
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 20,
          flexWrap: "wrap",
          background: "#f9fafb",
          border: "1px solid #e5e7eb",
          borderRadius: 10,
          padding: "12px 16px",
        }}
      >
        <Select
          placeholder="Izaberite lokaciju"
          value={selectedTokenId}
          onChange={setSelectedTokenId}
          loading={tokensLoading}
          style={{ minWidth: 220 }}
          optionLabelProp="label"
          options={tokens.map((tk) => ({
            value: tk._id,
            label: tk.name,
            desc: tk.locationId?.name,
          }))}
          optionRender={(opt) => (
            <div>
              <div style={{ fontWeight: 600 }}>{opt.data.label}</div>
              {opt.data.desc && (
                <div style={{ fontSize: 11, color: "#9ca3af" }}>{opt.data.desc}</div>
              )}
            </div>
          )}
          notFoundContent={
            tokensLoading
              ? "Učitavanje..."
              : "Nema podešenih lokacija. Dodajte ih u Podešavanja → Octopus."
          }
        />

        <RangePicker
          value={dateRange}
          onChange={(vals) => {
            if (vals?.[0] && vals?.[1]) setDateRange([vals[0], vals[1]]);
          }}
          format="DD.MM.YYYY"
          allowClear={false}
          style={{ minWidth: 240 }}
        />

        <Button
          icon={<ReloadOutlined />}
          onClick={handleFetch}
          loading={loading}
          disabled={!selectedTokenId}
        >
          Osvježi
        </Button>

        {shifts.length > 0 && (
          <Input
            placeholder="Pretraži po imenu..."
            prefix={<SearchOutlined style={{ color: "#9ca3af" }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ minWidth: 240 }}
          />
        )}
      </div>

      {/* Summary row */}
      {searched && !loading && displayedShifts.length > 0 && (
        <div
          style={{
            display: "flex",
            gap: 24,
            marginBottom: 16,
            flexWrap: "wrap",
            fontSize: 13,
            color: "#6b7280",
          }}
        >
          <span>
            Ukupno smena:{" "}
            <strong style={{ color: "#111827" }}>{displayedShifts.length}</strong>
          </span>
          <span>
            Aktivnih: <strong style={{ color: "#111827" }}>{activeCount}</strong>
          </span>
          <span>
            Završenih: <strong style={{ color: "#111827" }}>{finishedCount}</strong>
          </span>
        </div>
      )}

      <Table
        dataSource={displayedShifts}
        columns={columns}
        rowKey="Id"
        loading={loading}
        pagination={{ pageSize: 20, showSizeChanger: false }}
        onChange={(_pagination, _filters, sorter) => {
          setSortedInfo(Array.isArray(sorter) ? sorter[0] || {} : sorter);
        }}
        onRow={(record) => ({
          onClick: () => {
            if (canAssign && record.CheckOut && !isAssigned(record)) {
              setAssignRecord(record);
            }
          },
          style: {
            cursor:
              canAssign && record.CheckOut && !isAssigned(record)
                ? "pointer"
                : "default",
          },
        })}
        locale={{
          emptyText: searched
            ? "Nema smena za izabrani period i lokaciju"
            : "Izaberite lokaciju da učitate smene",
        }}
        scroll={{ x: 700 }}
      />

      <AssignOctopusShiftModal
        record={assignRecord}
        users={internalUsers}
        clientId={clientId}
        locationId={selectedLocationId}
        onClose={() => setAssignRecord(null)}
        onAssigned={(id) =>
          setAssignedIds((prev) => new Set(prev).add(id))
        }
      />
    </div>
  );
};

export default OctopusShifts;
